/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { PREDICTION_MIGRATOR_PROGRAM_ADDRESS } from '../programs';

export const CLAIM_DISCRIMINATOR = new Uint8Array([
  62, 198, 214, 193, 213, 159, 108, 210,
]);

export function getClaimDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(CLAIM_DISCRIMINATOR);
}

export type ClaimInstruction<
  TProgram extends string = typeof PREDICTION_MIGRATOR_PROGRAM_ADDRESS,
  TAccountMarket extends string | AccountMeta<string> = string,
  TAccountMarketAuthority extends string | AccountMeta<string> = string,
  TAccountPotVault extends string | AccountMeta<string> = string,
  TAccountWinnerMint extends string | AccountMeta<string> = string,
  TAccountQuoteMint extends string | AccountMeta<string> = string,
  TAccountEntryByMint extends string | AccountMeta<string> = string,
  TAccountClaimerWinnerAta extends string | AccountMeta<string> = string,
  TAccountClaimerQuoteAta extends string | AccountMeta<string> = string,
  TAccountClaimer extends string | AccountMeta<string> = string,
  TAccountReceipt extends string | AccountMeta<string> = string,
  TAccountPayer extends string | AccountMeta<string> = string,
  TAccountBaseTokenProgram extends string | AccountMeta<string> = string,
  TAccountQuoteTokenProgram extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    '11111111111111111111111111111111',
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountMarket extends string
        ? WritableAccount<TAccountMarket>
        : TAccountMarket,
      TAccountMarketAuthority extends string
        ? ReadonlyAccount<TAccountMarketAuthority>
        : TAccountMarketAuthority,
      TAccountPotVault extends string
        ? WritableAccount<TAccountPotVault>
        : TAccountPotVault,
      TAccountWinnerMint extends string
        ? WritableAccount<TAccountWinnerMint>
        : TAccountWinnerMint,
      TAccountQuoteMint extends string
        ? ReadonlyAccount<TAccountQuoteMint>
        : TAccountQuoteMint,
      TAccountEntryByMint extends string
        ? ReadonlyAccount<TAccountEntryByMint>
        : TAccountEntryByMint,
      TAccountClaimerWinnerAta extends string
        ? WritableAccount<TAccountClaimerWinnerAta>
        : TAccountClaimerWinnerAta,
      TAccountClaimerQuoteAta extends string
        ? WritableAccount<TAccountClaimerQuoteAta>
        : TAccountClaimerQuoteAta,
      TAccountClaimer extends string
        ? ReadonlySignerAccount<TAccountClaimer> &
            AccountSignerMeta<TAccountClaimer>
        : TAccountClaimer,
      TAccountReceipt extends string
        ? WritableAccount<TAccountReceipt>
        : TAccountReceipt,
      TAccountPayer extends string
        ? WritableSignerAccount<TAccountPayer> &
            AccountSignerMeta<TAccountPayer>
        : TAccountPayer,
      TAccountBaseTokenProgram extends string
        ? ReadonlyAccount<TAccountBaseTokenProgram>
        : TAccountBaseTokenProgram,
      TAccountQuoteTokenProgram extends string
        ? ReadonlyAccount<TAccountQuoteTokenProgram>
        : TAccountQuoteTokenProgram,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type ClaimInstructionData = {
  discriminator: ReadonlyUint8Array;
  burnAmount: bigint;
};

export type ClaimInstructionDataArgs = { burnAmount: number | bigint };

export function getClaimInstructionDataEncoder(): FixedSizeEncoder<ClaimInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['burnAmount', getU64Encoder()],
    ]),
    (value) => ({ ...value, discriminator: CLAIM_DISCRIMINATOR }),
  );
}

export function getClaimInstructionDataDecoder(): FixedSizeDecoder<ClaimInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['burnAmount', getU64Decoder()],
  ]);
}

export function getClaimInstructionDataCodec(): FixedSizeCodec<
  ClaimInstructionDataArgs,
  ClaimInstructionData
> {
  return combineCodec(
    getClaimInstructionDataEncoder(),
    getClaimInstructionDataDecoder(),
  );
}

export type ClaimAsyncInput<
  TAccountMarket extends string = string,
  TAccountMarketAuthority extends string = string,
  TAccountPotVault extends string = string,
  TAccountWinnerMint extends string = string,
  TAccountQuoteMint extends string = string,
  TAccountEntryByMint extends string = string,
  TAccountClaimerWinnerAta extends string = string,
  TAccountClaimerQuoteAta extends string = string,
  TAccountClaimer extends string = string,
  TAccountReceipt extends string = string,
  TAccountPayer extends string = string,
  TAccountBaseTokenProgram extends string = string,
  TAccountQuoteTokenProgram extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  /** Market PDA */
  market: Address<TAccountMarket>;
  marketAuthority?: Address<TAccountMarketAuthority>;
  /** Pot vault holding quote tokens */
  potVault: Address<TAccountPotVault>;
  /** Winner mint (must match market.winner_mint) */
  winnerMint: Address<TAccountWinnerMint>;
  /** Quote mint used for reward payouts */
  quoteMint: Address<TAccountQuoteMint>;
  /** EntryByMint for validation that this mint belongs to this market */
  entryByMint?: Address<TAccountEntryByMint>;
  /** Claimer's winner token account (source for burn) */
  claimerWinnerAta: Address<TAccountClaimerWinnerAta>;
  /** Claimer's quote token account (destination for rewards) */
  claimerQuoteAta: Address<TAccountClaimerQuoteAta>;
  /** Claimer wallet (signer) */
  claimer: TransactionSigner<TAccountClaimer>;
  /** Claim receipt PDA (created if first claim) */
  receipt?: Address<TAccountReceipt>;
  /** Payer for receipt creation (can be same as claimer) */
  payer: TransactionSigner<TAccountPayer>;
  baseTokenProgram: Address<TAccountBaseTokenProgram>;
  quoteTokenProgram: Address<TAccountQuoteTokenProgram>;
  systemProgram?: Address<TAccountSystemProgram>;
  burnAmount: ClaimInstructionDataArgs['burnAmount'];
};

export async function getClaimInstructionAsync<
  TAccountMarket extends string,
  TAccountMarketAuthority extends string,
  TAccountPotVault extends string,
  TAccountWinnerMint extends string,
  TAccountQuoteMint extends string,
  TAccountEntryByMint extends string,
  TAccountClaimerWinnerAta extends string,
  TAccountClaimerQuoteAta extends string,
  TAccountClaimer extends string,
  TAccountReceipt extends string,
  TAccountPayer extends string,
  TAccountBaseTokenProgram extends string,
  TAccountQuoteTokenProgram extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof PREDICTION_MIGRATOR_PROGRAM_ADDRESS,
>(
  input: ClaimAsyncInput<
    TAccountMarket,
    TAccountMarketAuthority,
    TAccountPotVault,
    TAccountWinnerMint,
    TAccountQuoteMint,
    TAccountEntryByMint,
    TAccountClaimerWinnerAta,
    TAccountClaimerQuoteAta,
    TAccountClaimer,
    TAccountReceipt,
    TAccountPayer,
    TAccountBaseTokenProgram,
    TAccountQuoteTokenProgram,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  ClaimInstruction<
    TProgramAddress,
    TAccountMarket,
    TAccountMarketAuthority,
    TAccountPotVault,
    TAccountWinnerMint,
    TAccountQuoteMint,
    TAccountEntryByMint,
    TAccountClaimerWinnerAta,
    TAccountClaimerQuoteAta,
    TAccountClaimer,
    TAccountReceipt,
    TAccountPayer,
    TAccountBaseTokenProgram,
    TAccountQuoteTokenProgram,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? PREDICTION_MIGRATOR_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    market: { value: input.market ?? null, isWritable: true },
    marketAuthority: {
      value: input.marketAuthority ?? null,
      isWritable: false,
    },
    potVault: { value: input.potVault ?? null, isWritable: true },
    winnerMint: { value: input.winnerMint ?? null, isWritable: true },
    quoteMint: { value: input.quoteMint ?? null, isWritable: false },
    entryByMint: { value: input.entryByMint ?? null, isWritable: false },
    claimerWinnerAta: {
      value: input.claimerWinnerAta ?? null,
      isWritable: true,
    },
    claimerQuoteAta: { value: input.claimerQuoteAta ?? null, isWritable: true },
    claimer: { value: input.claimer ?? null, isWritable: false },
    receipt: { value: input.receipt ?? null, isWritable: true },
    payer: { value: input.payer ?? null, isWritable: true },
    baseTokenProgram: {
      value: input.baseTokenProgram ?? null,
      isWritable: false,
    },
    quoteTokenProgram: {
      value: input.quoteTokenProgram ?? null,
      isWritable: false,
    },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.marketAuthority.value) {
    accounts.marketAuthority.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            109, 97, 114, 107, 101, 116, 95, 97, 117, 116, 104, 111, 114, 105,
            116, 121,
          ]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'market',
            accounts.market.value,
          ),
        ),
      ],
    });
  }
  if (!accounts.entryByMint.value) {
    accounts.entryByMint.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            101, 110, 116, 114, 121, 95, 98, 121, 95, 109, 105, 110, 116,
          ]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'market',
            accounts.market.value,
          ),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'winnerMint',
            accounts.winnerMint.value,
          ),
        ),
      ],
    });
  }
  if (!accounts.receipt.value) {
    accounts.receipt.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([114, 101, 99, 101, 105, 112, 116]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'market',
            accounts.market.value,
          ),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'claimer',
            accounts.claimer.value,
          ),
        ),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('market', accounts.market),
      getAccountMeta('marketAuthority', accounts.marketAuthority),
      getAccountMeta('potVault', accounts.potVault),
      getAccountMeta('winnerMint', accounts.winnerMint),
      getAccountMeta('quoteMint', accounts.quoteMint),
      getAccountMeta('entryByMint', accounts.entryByMint),
      getAccountMeta('claimerWinnerAta', accounts.claimerWinnerAta),
      getAccountMeta('claimerQuoteAta', accounts.claimerQuoteAta),
      getAccountMeta('claimer', accounts.claimer),
      getAccountMeta('receipt', accounts.receipt),
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('baseTokenProgram', accounts.baseTokenProgram),
      getAccountMeta('quoteTokenProgram', accounts.quoteTokenProgram),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getClaimInstructionDataEncoder().encode(
      args as ClaimInstructionDataArgs,
    ),
    programAddress,
  } as ClaimInstruction<
    TProgramAddress,
    TAccountMarket,
    TAccountMarketAuthority,
    TAccountPotVault,
    TAccountWinnerMint,
    TAccountQuoteMint,
    TAccountEntryByMint,
    TAccountClaimerWinnerAta,
    TAccountClaimerQuoteAta,
    TAccountClaimer,
    TAccountReceipt,
    TAccountPayer,
    TAccountBaseTokenProgram,
    TAccountQuoteTokenProgram,
    TAccountSystemProgram
  >);
}

export type ClaimInput<
  TAccountMarket extends string = string,
  TAccountMarketAuthority extends string = string,
  TAccountPotVault extends string = string,
  TAccountWinnerMint extends string = string,
  TAccountQuoteMint extends string = string,
  TAccountEntryByMint extends string = string,
  TAccountClaimerWinnerAta extends string = string,
  TAccountClaimerQuoteAta extends string = string,
  TAccountClaimer extends string = string,
  TAccountReceipt extends string = string,
  TAccountPayer extends string = string,
  TAccountBaseTokenProgram extends string = string,
  TAccountQuoteTokenProgram extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  /** Market PDA */
  market: Address<TAccountMarket>;
  marketAuthority: Address<TAccountMarketAuthority>;
  /** Pot vault holding quote tokens */
  potVault: Address<TAccountPotVault>;
  /** Winner mint (must match market.winner_mint) */
  winnerMint: Address<TAccountWinnerMint>;
  /** Quote mint used for reward payouts */
  quoteMint: Address<TAccountQuoteMint>;
  /** EntryByMint for validation that this mint belongs to this market */
  entryByMint: Address<TAccountEntryByMint>;
  /** Claimer's winner token account (source for burn) */
  claimerWinnerAta: Address<TAccountClaimerWinnerAta>;
  /** Claimer's quote token account (destination for rewards) */
  claimerQuoteAta: Address<TAccountClaimerQuoteAta>;
  /** Claimer wallet (signer) */
  claimer: TransactionSigner<TAccountClaimer>;
  /** Claim receipt PDA (created if first claim) */
  receipt: Address<TAccountReceipt>;
  /** Payer for receipt creation (can be same as claimer) */
  payer: TransactionSigner<TAccountPayer>;
  baseTokenProgram: Address<TAccountBaseTokenProgram>;
  quoteTokenProgram: Address<TAccountQuoteTokenProgram>;
  systemProgram?: Address<TAccountSystemProgram>;
  burnAmount: ClaimInstructionDataArgs['burnAmount'];
};

export function getClaimInstruction<
  TAccountMarket extends string,
  TAccountMarketAuthority extends string,
  TAccountPotVault extends string,
  TAccountWinnerMint extends string,
  TAccountQuoteMint extends string,
  TAccountEntryByMint extends string,
  TAccountClaimerWinnerAta extends string,
  TAccountClaimerQuoteAta extends string,
  TAccountClaimer extends string,
  TAccountReceipt extends string,
  TAccountPayer extends string,
  TAccountBaseTokenProgram extends string,
  TAccountQuoteTokenProgram extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof PREDICTION_MIGRATOR_PROGRAM_ADDRESS,
>(
  input: ClaimInput<
    TAccountMarket,
    TAccountMarketAuthority,
    TAccountPotVault,
    TAccountWinnerMint,
    TAccountQuoteMint,
    TAccountEntryByMint,
    TAccountClaimerWinnerAta,
    TAccountClaimerQuoteAta,
    TAccountClaimer,
    TAccountReceipt,
    TAccountPayer,
    TAccountBaseTokenProgram,
    TAccountQuoteTokenProgram,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): ClaimInstruction<
  TProgramAddress,
  TAccountMarket,
  TAccountMarketAuthority,
  TAccountPotVault,
  TAccountWinnerMint,
  TAccountQuoteMint,
  TAccountEntryByMint,
  TAccountClaimerWinnerAta,
  TAccountClaimerQuoteAta,
  TAccountClaimer,
  TAccountReceipt,
  TAccountPayer,
  TAccountBaseTokenProgram,
  TAccountQuoteTokenProgram,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? PREDICTION_MIGRATOR_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    market: { value: input.market ?? null, isWritable: true },
    marketAuthority: {
      value: input.marketAuthority ?? null,
      isWritable: false,
    },
    potVault: { value: input.potVault ?? null, isWritable: true },
    winnerMint: { value: input.winnerMint ?? null, isWritable: true },
    quoteMint: { value: input.quoteMint ?? null, isWritable: false },
    entryByMint: { value: input.entryByMint ?? null, isWritable: false },
    claimerWinnerAta: {
      value: input.claimerWinnerAta ?? null,
      isWritable: true,
    },
    claimerQuoteAta: { value: input.claimerQuoteAta ?? null, isWritable: true },
    claimer: { value: input.claimer ?? null, isWritable: false },
    receipt: { value: input.receipt ?? null, isWritable: true },
    payer: { value: input.payer ?? null, isWritable: true },
    baseTokenProgram: {
      value: input.baseTokenProgram ?? null,
      isWritable: false,
    },
    quoteTokenProgram: {
      value: input.quoteTokenProgram ?? null,
      isWritable: false,
    },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('market', accounts.market),
      getAccountMeta('marketAuthority', accounts.marketAuthority),
      getAccountMeta('potVault', accounts.potVault),
      getAccountMeta('winnerMint', accounts.winnerMint),
      getAccountMeta('quoteMint', accounts.quoteMint),
      getAccountMeta('entryByMint', accounts.entryByMint),
      getAccountMeta('claimerWinnerAta', accounts.claimerWinnerAta),
      getAccountMeta('claimerQuoteAta', accounts.claimerQuoteAta),
      getAccountMeta('claimer', accounts.claimer),
      getAccountMeta('receipt', accounts.receipt),
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('baseTokenProgram', accounts.baseTokenProgram),
      getAccountMeta('quoteTokenProgram', accounts.quoteTokenProgram),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getClaimInstructionDataEncoder().encode(
      args as ClaimInstructionDataArgs,
    ),
    programAddress,
  } as ClaimInstruction<
    TProgramAddress,
    TAccountMarket,
    TAccountMarketAuthority,
    TAccountPotVault,
    TAccountWinnerMint,
    TAccountQuoteMint,
    TAccountEntryByMint,
    TAccountClaimerWinnerAta,
    TAccountClaimerQuoteAta,
    TAccountClaimer,
    TAccountReceipt,
    TAccountPayer,
    TAccountBaseTokenProgram,
    TAccountQuoteTokenProgram,
    TAccountSystemProgram
  >);
}

export type ParsedClaimInstruction<
  TProgram extends string = typeof PREDICTION_MIGRATOR_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Market PDA */
    market: TAccountMetas[0];
    marketAuthority: TAccountMetas[1];
    /** Pot vault holding quote tokens */
    potVault: TAccountMetas[2];
    /** Winner mint (must match market.winner_mint) */
    winnerMint: TAccountMetas[3];
    /** Quote mint used for reward payouts */
    quoteMint: TAccountMetas[4];
    /** EntryByMint for validation that this mint belongs to this market */
    entryByMint: TAccountMetas[5];
    /** Claimer's winner token account (source for burn) */
    claimerWinnerAta: TAccountMetas[6];
    /** Claimer's quote token account (destination for rewards) */
    claimerQuoteAta: TAccountMetas[7];
    /** Claimer wallet (signer) */
    claimer: TAccountMetas[8];
    /** Claim receipt PDA (created if first claim) */
    receipt: TAccountMetas[9];
    /** Payer for receipt creation (can be same as claimer) */
    payer: TAccountMetas[10];
    baseTokenProgram: TAccountMetas[11];
    quoteTokenProgram: TAccountMetas[12];
    systemProgram: TAccountMetas[13];
  };
  data: ClaimInstructionData;
};

export function parseClaimInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedClaimInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 14) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 14,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      market: getNextAccount(),
      marketAuthority: getNextAccount(),
      potVault: getNextAccount(),
      winnerMint: getNextAccount(),
      quoteMint: getNextAccount(),
      entryByMint: getNextAccount(),
      claimerWinnerAta: getNextAccount(),
      claimerQuoteAta: getNextAccount(),
      claimer: getNextAccount(),
      receipt: getNextAccount(),
      payer: getNextAccount(),
      baseTokenProgram: getNextAccount(),
      quoteTokenProgram: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getClaimInstructionDataDecoder().decode(instruction.data),
  };
}
